import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { supabase, SUPABASE_LISTO } from '../lib/supabase';
import { useApp } from '../store/useApp';
import { Logo } from '../components/Logo';
import { LangSelector } from '../components/LangSelector';

const slugify = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40);

export default function Login() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { session, tenants, cargarTenants, revisarCambioPassword } = useApp();
  const [sp] = useSearchParams();
  // El ÚNICO CTA de la landing es "Pruébalo 7 días gratis" → /login?m=signup: debe abrir REGISTRO.
  const [modo, setModo] = useState(sp.get('m') === 'signup' ? 'signup' : 'login'); // login | magic | signup
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [enviado, setEnviado] = useState(false);
  const [confirmar, setConfirmar] = useState(false);
  // Paso 2 (datos de empresa para el trial)
  const [nombre, setNombre] = useState('');
  const [slug, setSlug] = useState('');
  const [telefono, setTelefono] = useState('');
  const [sector, setSector] = useState('');
  const [msg, setMsg] = useState('');
  const [cargando, setCargando] = useState(false);

  const necesitaEmpresa = session && tenants.length === 0;
  // Un usuario con empresa puede crear OTRA (?m=empresa desde el selector de la cabecera).
  const quiereEmpresa = sp.get('m') === 'empresa';
  const mostrarEmpresa = session && (tenants.length === 0 || quiereEmpresa);
  const tieneOtras = tenants.length > 0;

  // Si ya hay sesión y tenant (p.ej. recarga sobre /login), entra al panel en vez de quedarse en el formulario.
  // Excepción: si viene a crear OTRA empresa (?m=empresa), no le echamos al panel.
  useEffect(() => {
    if (session && tenants.length > 0 && !quiereEmpresa) nav('/', { replace: true });
  }, [session, tenants.length, quiereEmpresa, nav]);

  const entrar = async (e) => {
    e.preventDefault(); setMsg(''); setCargando(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) { setMsg(error.message); setCargando(false); return; }
    const debe = await revisarCambioPassword();
    setCargando(false);
    nav(debe ? '/cambiar-password' : '/', { replace: true });
  };

  const enviarLink = async (e) => {
    e.preventDefault(); setMsg(''); setCargando(true);
    const { error } = await supabase.auth.signInWithOtp({ email, options: { emailRedirectTo: window.location.origin + '/login' } });
    setCargando(false);
    if (error) setMsg(error.message); else setEnviado(true);
  };

  const registrar = async (e) => {
    e.preventDefault(); setMsg(''); setCargando(true);
    const { data, error } = await supabase.auth.signUp({ email, password, options: { emailRedirectTo: window.location.origin + '/login' } });
    setCargando(false);
    if (error) {
      // Supabase devuelve "User already registered" cuando el correo existe.
      if (/already|registrado|registered|exist/i.test(error.message)) { setModo('existe'); return; }
      setMsg(error.message); return;
    }
    // Anti-enumeración de Supabase: si el correo ya existe, devuelve user con identities vacío.
    if (data?.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) { setModo('existe'); return; }
    if (data.session) { setModo('login'); } // sin confirmación → ya hay sesión, saltará a paso 2
    else { setConfirmar(true); } // requiere confirmar correo
  };

  const recuperar = async (e) => {
    e?.preventDefault?.(); setMsg(''); setCargando(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: window.location.origin + '/cambiar-password' });
    setCargando(false);
    if (error) setMsg(error.message); else setEnviado(true);
  };

  const crearEmpresa = async (e) => {
    e.preventDefault(); setMsg(''); setCargando(true);
    const { data, error } = await supabase.rpc('crear_tenant', {
      p_slug: slugify(slug || nombre), p_nombre: nombre,
      p_idioma: localStorage.getItem('hueco-idioma') || 'es',
      p_empresa: nombre, p_telefono: telefono || null, p_sector: sector || null, p_origen: 'web',
    });
    if (error) { setMsg(error.message); setCargando(false); return; }
    // Deja activa la empresa recién creada (importante cuando el usuario ya tenía otras).
    const nuevoId = typeof data === 'string' ? data : (data?.tenant_id || data?.id || null);
    if (nuevoId) localStorage.setItem('hueco-tenant', nuevoId);
    await cargarTenants();
    setCargando(false);
    nav('/onboarding'); // al asistente: que vea su almacén en los primeros 10 minutos
  };

  return (
    <div className="center-page">
      <div className="card stack" style={{ width: 440, maxWidth: '100%' }} data-testid="login-card">
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <Logo endoso />
          <LangSelector />
        </div>
        {!SUPABASE_LISTO && <div className="banner small" data-testid="sin-config">{t('login.sin_config')}</div>}

        {necesitaEmpresa || mostrarEmpresa ? (
          <form className="stack" onSubmit={crearEmpresa} data-testid="empresa-form">
            <h1>{tieneOtras ? t('login.empresa_otra_titulo') : t('login.registro_titulo')}</h1>
            <p className="muted small">{tieneOtras ? t('login.empresa_otra_sub') : t('login.registro_subtitulo')}</p>
            <div className="field"><label className="label">{t('login.registro_nombre')}</label>
              <input value={nombre} onChange={(e) => { setNombre(e.target.value); if (!slug) setSlug(slugify(e.target.value)); }} required data-testid="empresa-nombre" /></div>
            <div className="field"><label className="label">{t('login.registro_slug')}</label>
              <input value={slug} onChange={(e) => setSlug(slugify(e.target.value))} placeholder="mi-almacen" data-testid="empresa-slug" /></div>
            <div className="field"><label className="label">{t('login.registro_telefono')}</label>
              <input value={telefono} onChange={(e) => setTelefono(e.target.value)} data-testid="empresa-telefono" /></div>
            <div className="field"><label className="label">{t('login.registro_sector')}</label>
              <input value={sector} onChange={(e) => setSector(e.target.value)} placeholder={t('login.sector_ph')} data-testid="empresa-sector" /></div>
            <button className="btn primary op" disabled={cargando || !nombre} data-testid="crear-empresa-btn">{t('login.registro_crear')}</button>
            {tieneOtras && <button type="button" className="btn ghost" onClick={() => nav('/')} data-testid="empresa-cancelar-btn">{t('comun.cancelar')}</button>}
            {msg && <p className="small" style={{ color: 'var(--ocupada)' }} data-testid="login-error">{msg}</p>}
          </form>
        ) : confirmar ? (
          <div className="banner" data-testid="signup-confirma">{t('login.signup_ok_confirma')}</div>
        ) : modo === 'magic' ? (
          <form className="stack" onSubmit={enviarLink}>
            <h1>{t('login.titulo')}</h1>
            {enviado ? <div className="banner" data-testid="magic-enviado">{t('login.enviado')}</div> : (
              <>
                <div className="field"><label className="label">{t('login.email')}</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="email-input" /></div>
                <button className="btn primary op" disabled={cargando || !SUPABASE_LISTO} data-testid="magic-link-btn">{t('login.enviar')}</button>
                <button type="button" className="btn ghost" onClick={() => setModo('login')} data-testid="ir-password">{t('login.usar_password')}</button>
              </>
            )}
            {msg && <p className="small" style={{ color: 'var(--ocupada)' }} data-testid="login-error">{msg}</p>}
          </form>
        ) : modo === 'reset' ? (
          <form className="stack" onSubmit={recuperar}>
            <h1>{t('login.reset_titulo', 'Recuperar contraseña')}</h1>
            {enviado ? (
              <div className="banner" data-testid="reset-enviado">{t('login.reset_enviado', 'Si el correo existe, te hemos enviado un enlace para cambiar tu contraseña. Revisa tu bandeja.')}</div>
            ) : (
              <>
                <p className="muted small">{t('login.reset_desc', 'Escribe tu correo y te enviamos un enlace para poner una contraseña nueva.')}</p>
                <div className="field"><label className="label">{t('login.email')}</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="email-input" /></div>
                <button className="btn primary op" disabled={cargando || !email || !SUPABASE_LISTO} data-testid="reset-enviar-btn">{t('login.reset_enviar', 'Enviar enlace')}</button>
                <button type="button" className="btn ghost" onClick={() => { setModo('login'); setMsg(''); }} data-testid="reset-volver">{t('login.usar_password', 'Usar contraseña')}</button>
              </>
            )}
            {msg && <p className="small" style={{ color: 'var(--ocupada)' }} data-testid="login-error">{msg}</p>}
          </form>
        ) : modo === 'existe' ? (
          <div className="stack" data-testid="email-existe">
            <h1>{t('login.existe_titulo', 'Ya tienes cuenta')}</h1>
            <div className="banner aviso">{t('login.existe_desc', 'Ya existe una cuenta con este correo. ¿Quieres entrar o recuperar tu contraseña?')}</div>
            <button className="btn primary op" onClick={() => { setModo('login'); setMsg(''); }} data-testid="existe-entrar">{t('login.entrar', 'Entrar')}</button>
            <button className="btn ghost" onClick={() => { setEnviado(false); setModo('reset'); setMsg(''); }} data-testid="existe-recuperar">{t('login.reset_titulo', 'Recuperar contraseña')}</button>
          </div>
        ) : (
          <form className="stack" onSubmit={modo === 'signup' ? registrar : entrar}>
            <h1>{modo === 'signup' ? t('login.signup_titulo') : t('login.titulo')}</h1>
            <div className="field"><label className="label">{t('login.email')}</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="email-input" /></div>
            <div className="field"><label className="label">{t('login.password')}</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="password-input" /></div>
            <button className="btn primary op" disabled={cargando || !SUPABASE_LISTO} data-testid={modo === 'signup' ? 'signup-btn' : 'password-login-btn'}>
              {modo === 'signup' ? t('login.trial_cta') : t('login.entrar')}
            </button>
            {modo === 'signup' && (
              <p className="muted" style={{ fontSize: 12, lineHeight: 1.5, marginTop: -4 }} data-testid="signup-consent">
                {t('login.consent_pre')}
                <Link to="/terminos" data-testid="consent-terminos">{t('login.consent_terminos')}</Link>
                {t('login.consent_y')}
                <Link to="/privacidad" data-testid="consent-privacidad">{t('login.consent_privacidad')}</Link>
                {t('login.consent_fin')}
              </p>
            )}
            <div className="stack" style={{ gap: 8, alignItems: 'center', marginTop: 4 }}>
              <p className="small" data-testid="toggle-signup-prompt" style={{ margin: 0 }}>
                {modo === 'signup' ? t('login.con_cuenta_pre') : t('login.sin_cuenta_pre')}{' '}
                <button type="button" onClick={() => { setModo(modo === 'signup' ? 'login' : 'signup'); setMsg(''); }} data-testid="toggle-signup"
                  style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', color: 'var(--cobalt, #2148FF)', fontWeight: 700, textDecoration: 'underline' }}>
                  {modo === 'signup' ? t('login.con_cuenta_cta') : t('login.sin_cuenta_cta')}
                </button>
              </p>
              <button type="button" className="btn ghost sm" onClick={() => { setModo('magic'); setMsg(''); }} data-testid="ir-magic">{t('login.o_magic')}</button>
            </div>
            {modo !== 'signup' && (
              <button type="button" className="btn ghost sm" style={{ margin: '0 auto' }} onClick={() => { setEnviado(false); setModo('reset'); setMsg(''); }} data-testid="olvide-password">
                {t('login.olvide', 'He olvidado mi contraseña')}
              </button>
            )}
            {msg && <p className="small" style={{ color: 'var(--ocupada)' }} data-testid="login-error">{msg}</p>}
          </form>
        )}

        <p className="foot"><span className="exp">HUEC▣</span> · {t('marca.endoso')}</p>
      </div>
    </div>
  );
}
